import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import BatchTradeDialog, { tradeTypeLabel } from "@/components/benchmark/BatchTradeDialog";
import FundPicker from "@/components/benchmark/FundPicker";
import { useRangeFunds } from "@/components/benchmark/rangeFunds";



import { tradeDirectionVariant, tradeDirectionHint } from "@/lib/tradeDirection";
interface Trade {
  id: string;
  trade_date: string;
  fund_id: string;
  direction: string;
  portfolio_1_included: boolean; portfolio_1_pct: number;
  portfolio_2_included: boolean; portfolio_2_pct: number;
  portfolio_3_included: boolean; portfolio_3_pct: number;
  portfolio_4_included: boolean; portfolio_4_pct: number;
  portfolio_5_included: boolean; portfolio_5_pct: number;
  fund_name_override: string | null;
  notes: string | null;
  created_at: string;
  trade_type: string | null;
  batch_id: string | null;
}

function parseOverride(s: string | null | undefined) {
  if (!s) return { manager: "", fund: "", klass: "" };
  const parts = s.split(":").map((p) => p.trim());
  if (parts.length >= 3) return { manager: parts[0], fund: parts[1], klass: parts[2] };
  return { manager: "", fund: s, klass: "" };
}

export default function RTradeLog() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [fundMode, setFundMode] = useState<"existing" | "new">("existing");
  const [newHolding, setNewHolding] = useState({ fund_manager: "", holding_name: "", isin: "" });
  const [form, setForm] = useState({
    trade_date: new Date().toISOString().split("T")[0],
    fund_id: "",
    direction: "BUY" as "BUY" | "SELL",
    portfolio_1_included: false, portfolio_1_pct: 0,
    portfolio_2_included: false, portfolio_2_pct: 0,
    portfolio_3_included: false, portfolio_3_pct: 0,
    portfolio_4_included: false, portfolio_4_pct: 0,
    portfolio_5_included: false, portfolio_5_pct: 0,
    notes: "",
  });

  const { data: trades } = useQuery({
    queryKey: ["ba-r-trades"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_r_trades").select("*").order("trade_date", { ascending: false });
      if (error) throw error;
      return data as Trade[];
    },
  });

  // Selection is resolved from ba_r_cbam_fund_weights, not from is_current, so a fund sold out of
  // R stays re-selectable and can be bought back. See rangeFunds.ts.
  const { selectable: funds, all: allFunds } = useRangeFunds("ba_r_cbam_fund_weights");

  const addTradeMutation = useMutation({
    mutationFn: async () => {
      let fundId = form.fund_id;
      if (fundMode === "new") {
        const manager = newHolding.fund_manager.trim();
        const holding = newHolding.holding_name.trim();
        const isin = newHolding.isin.trim().toUpperCase();
        if (!manager || !holding) throw new Error("Fund Manager and Holding Name are required");
        if (!isin) throw new Error("ISIN is required");
        const fullName = `${manager} ${holding}`;
        const { data: inserted, error: insErr } = await supabase
          .from("ba_cbam_funds")
          .insert({ fund_name: fullName, fe_fund_name: fullName, fund_manager: manager, isin, is_current: true })
          .select("id")
          .single();
        if (insErr) throw insErr;
        fundId = inserted.id;
      }
      if (!fundId) throw new Error("Please select or add a fund");
      const { error } = await supabase.from("ba_r_trades").insert({ ...form, fund_id: fundId });
      if (error) throw error;

      // Weight rows carry the TRADE date, not today's. The form captures an editable trade date
      // and writes it to ba_r_trades; stamping the weight rows with new Date() dated the position
      // change differently from the trade that caused it, so a back-dated trade silently took
      // effect today. BatchTradeDialog already stamped tradeDate; this matches it.
      const effectiveDate = form.trade_date;
      const sign = form.direction === "BUY" ? 1 : -1;
      const portfolios = [1, 2, 3, 4, 5] as const;

      // Latest weight per portfolio AS OF the trade date — rows dated after the trade are excluded
      // so a back-dated trade cannot be computed from its own future.
      const { data: existingWeights } = await supabase
        .from("ba_r_cbam_fund_weights")
        .select("portfolio, weight_pct, effective_date")
        .eq("fund_id", fundId)
        .lte("effective_date", effectiveDate)
        .order("effective_date", { ascending: false });

      const latestByPortfolio = new Map<number, number>();
      for (const w of existingWeights || []) {
        if (!latestByPortfolio.has(w.portfolio)) {
          latestByPortfolio.set(w.portfolio, Number(w.weight_pct) || 0);
        }
      }

      const newWeights: Record<number, number> = {};
      for (const p of portfolios) {
        const current = latestByPortfolio.get(p) || 0;
        const included = form[`portfolio_${p}_included` as keyof typeof form] as boolean;
        const pct = form[`portfolio_${p}_pct` as keyof typeof form] as number;
        const delta = included ? sign * Number(pct || 0) : 0;
        const next = Math.max(0, current + delta);
        newWeights[p] = next;

        if (included) {
          const { error: wErr } = await supabase
            .from("ba_r_cbam_fund_weights")
            .upsert(
              { fund_id: fundId, portfolio: p, weight_pct: next, effective_date: effectiveDate },
              { onConflict: "fund_id,portfolio,effective_date" }
            );
          if (wErr) throw wErr;
        }
      }

      // R-side full SELL: record the exit, never delete. ba_r_cbam_fund_weights is
      // effective-dated and every historical reconstruction reads "the latest row per fund on
      // or before date X" (src/lib/blendedPortfolioMatrix.ts), so deleting the fund's rows
      // erases its entire R history. The schema's exit convention is an explicit
      // weight_pct = 0 row at the exit date; the upserts above already wrote one for every
      // included portfolio, so only stamp the rest. Funds and sector mappings are SHARED with
      // P and are never touched from here.
      if (form.direction === "SELL") {
        const finalWeights = portfolios.map((p) =>
          (form[`portfolio_${p}_included` as keyof typeof form] as boolean)
            ? newWeights[p]
            : (latestByPortfolio.get(p) || 0)
        );
        if (finalWeights.every((w) => w <= 0.0001)) {
          const exitRows = portfolios
            .filter((p) => !(form[`portfolio_${p}_included` as keyof typeof form] as boolean))
            .map((p) => ({ fund_id: fundId, portfolio: p, weight_pct: 0, effective_date: effectiveDate }));
          if (exitRows.length) {
            const { error: xErr } = await supabase
              .from("ba_r_cbam_fund_weights")
              .upsert(exitRows, { onConflict: "fund_id,portfolio,effective_date" });
            if (xErr) throw xErr;
          }
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ba-r-trades"] });
      queryClient.invalidateQueries({ queryKey: ["ba-cbam-funds-active"] });
      setShowAdd(false);
      setFundMode("existing");
      setNewHolding({ fund_manager: "", holding_name: "", isin: "" });
      toast.success("Trade logged");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Name lookup for the history table spans EVERY fund, not just selectable ones — a trade on a
  // fund since exited must still render its name rather than "Unknown".
  const fundMap = new Map(allFunds.map((f) => [f.id, f]));
  const selectedFund = fundMap.get(form.fund_id);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">R Trade Log</h1>
          <p className="text-sm text-muted-foreground">Record and review R portfolio trades</p>
        </div>
        <div className="flex items-center gap-2">
        <BatchTradeDialog
          tradesTable="ba_r_trades"
          weightsTable="ba_r_cbam_fund_weights"
          portfolios={[1,2,3,4,5].map((n) => ({ num: n, label: `R${n}` }))}
          funds={funds}
          invalidateKeys={[["ba-r-trades"], ["ba-cbam-funds-active"], ["ba-r-fund-weights-all"], ["ba-r-cbam-funds-all"]]}
        />
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="h-4 w-4 mr-1" /> Log Trade</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Log New Trade</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Trade Date</Label>
                  <Input type="date" value={form.trade_date} onChange={(e) => setForm({ ...form, trade_date: e.target.value })} />
                </div>
                <div>
                  <Label>Direction</Label>
                  <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v as "BUY" | "SELL" })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BUY">BUY</SelectItem>
                      <SelectItem value="SELL">SELL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Fund</Label>
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs"
                    onClick={() => setFundMode(fundMode === "existing" ? "new" : "existing")}>
                    {fundMode === "existing" ? "+ Add new holding" : "← Use existing fund"}
                  </Button>
                </div>
                {fundMode === "existing" ? (
                  <>
                    <FundPicker value={form.fund_id} onValueChange={(v) => setForm({ ...form, fund_id: v })} funds={funds} />
                    {selectedFund?.previouslyHeldInRange && (
                      <p className="text-xs text-amber-600 dark:text-amber-500">
                        Previously held in R and sold out. A BUY re-opens the position from the trade
                        date — no need to re-create the fund.
                      </p>
                    )}
                  </>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Fund Manager</Label>
                      <Input value={newHolding.fund_manager} onChange={(e) => setNewHolding({ ...newHolding, fund_manager: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Holding Name</Label>
                      <Input value={newHolding.holding_name} onChange={(e) => setNewHolding({ ...newHolding, holding_name: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">ISIN <span className="text-destructive">*</span></Label>
                      <Input value={newHolding.isin} onChange={(e) => setNewHolding({ ...newHolding, isin: e.target.value.toUpperCase() })} maxLength={12} />
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Portfolio Allocations</Label>
                {[1, 2, 3, 4, 5].map((p) => {
                  const inclKey = `portfolio_${p}_included` as keyof typeof form;
                  const pctKey = `portfolio_${p}_pct` as keyof typeof form;
                  return (
                    <div key={p} className="flex items-center gap-3">
                      <Checkbox checked={form[inclKey] as boolean} onCheckedChange={(v) => setForm({ ...form, [inclKey]: !!v })} />
                      <span className="text-sm font-medium w-6">R{p}</span>
                      <Input type="number" step="0.01" className="w-24 h-8 text-xs"
                        value={form[pctKey] as number}
                        onChange={(e) => setForm({ ...form, [pctKey]: parseFloat(e.target.value) || 0 })}
                        disabled={!form[inclKey]} />
                      <span className="text-xs text-muted-foreground">%</span>
                    </div>
                  );
                })}
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="h-20" />
              </div>
              <Button onClick={() => addTradeMutation.mutate()}
                disabled={fundMode === "existing" ? !form.fund_id : !newHolding.fund_manager.trim() || !newHolding.holding_name.trim() || !newHolding.isin.trim()}>
                Save Trade
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Type</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Manager</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Fund</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Fund Class</th>
                <th className="text-left p-3 font-medium text-muted-foreground">ISIN</th>
                <th className="text-center p-3 font-medium text-muted-foreground">Dir</th>
                {[1, 2, 3, 4, 5].map((p) => (
                  <th key={p} className="text-center p-3 font-medium text-muted-foreground">R{p}</th>
                ))}
                <th className="text-left p-3 font-medium text-muted-foreground">Notes</th>
              </tr>
            </thead>
            <tbody>
              {trades?.map((trade, idx) => {
                const f = fundMap.get(trade.fund_id);
                const parsed = parseOverride(trade.fund_name_override);
                const manager = f?.fund_manager || parsed.manager || "—";
                const fundName = f?.fund_name || parsed.fund || "Unknown";
                const klass = f?.fund_class || parsed.klass || "—";
                const isin = f?.isin || "—";
                return (
                  <tr key={trade.id} className={cn("border-b", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                    <td className="p-3 text-xs">{new Date(trade.trade_date).toLocaleDateString("en-GB")}</td>
                    <td className="p-3 text-xs">
                      {trade.trade_type ? (
                        <Badge variant="outline" className="text-[10px]">{tradeTypeLabel(trade.trade_type)}</Badge>
                      ) : "—"}
                    </td>
                    <td className="p-3 text-xs">{manager}</td>
                    <td className="p-3 text-xs font-medium">{fundName}</td>
                    <td className="p-3 text-xs">{klass}</td>
                    <td className="p-3 text-xs font-mono text-muted-foreground">{isin}</td>
                    <td className="p-3 text-center">
                      <Badge variant={tradeDirectionVariant(trade.direction)} title={tradeDirectionHint(trade.direction)} className="text-[10px]">
                        {trade.direction}
                      </Badge>
                    </td>
                    {[1, 2, 3, 4, 5].map((p) => {
                      const incl = trade[`portfolio_${p}_included` as keyof Trade] as boolean;
                      const pct = trade[`portfolio_${p}_pct` as keyof Trade] as number;
                      return (
                        <td key={p} className="p-3 text-center text-xs">
                          {incl ? `${Number(pct).toFixed(2)}%` : "—"}
                        </td>
                      );
                    })}
                    <td className="p-3 text-xs text-muted-foreground max-w-[200px] truncate">{trade.notes || "—"}</td>
                  </tr>
                );
              })}
              {(!trades || trades.length === 0) && (
                <tr><td colSpan={13} className="p-8 text-center text-muted-foreground">No trades logged yet.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
